import type { Booking, Lead } from "./supabase";
import { humanise } from "./format";

/** Controllers needed for a booking (crew size x crews). */
export const crewNeeded = (b: Booking) => b.crew_size * b.crews;

/** "3-person" or "2 x 2-person". */
export const crewLabel = (b: Booking) => (b.crews > 1 ? `${b.crews} x ${b.crew_size}-person` : `${b.crew_size}-person`);

/**
 * Controllers still to allocate on a confirmed or allocated booking, or null when
 * not applicable (requested, completed, cancelled, or allocations not loaded).
 */
export function crewShortfall(b: Booking): number | null {
  if (!b.allocations || (b.status !== "confirmed" && b.status !== "allocated")) return null;
  const short = crewNeeded(b) - b.allocations.length;
  return short > 0 ? short : null;
}

const SOURCE_LABEL: Record<string, string> = { instant_quote: "Instant quote", website: "Website form" };
export const leadSource = (s: string) => SOURCE_LABEL[s] ?? humanise(s);

/** "2-person night, 10 hrs + TGS" from the lead's estimate input, else its services. */
export function leadJob(l: Lead): string {
  const labour = l.estimate_input?.labour;
  const extras = (l.estimate_input?.services ?? []).map((s) => s.code);
  const parts: string[] = [];
  if (labour?.crewSize) {
    const shift = labour.shiftType ? ` ${humanise(labour.shiftType).toLowerCase()}` : "";
    const crews = labour.crews && labour.crews > 1 ? `${labour.crews} x ` : "";
    parts.push(`${crews}${labour.crewSize}-person${shift}${labour.hours ? `, ${labour.hours} hrs` : ""}`);
  }
  if (extras.length) parts.push(parts.length ? `+ ${extras.join(", ")}` : extras.join(", "));
  if (parts.length) return parts.join(" ");
  if (l.services.length) return l.services.map(humanise).join(", ");
  return l.details?.length ? "Website enquiry" : "No job details";
}
